import type {
  healthRiskSummarySchema,
  itemDependencyStateSchema,
  itemHealthByItemIdsResponseSchema,
  itemHealthResponseSchema,
  itemHealthRiskSchema,
} from '@tempo-io/reporting-store-dto';
import {
  dependencyItemCauseSummarySchema,
  itemsHealthMetricsSchema,
} from '@tempo-io/reporting-store-dto';
import { z } from 'zod';

const dependencyItemCauseSchema = dependencyItemCauseSummarySchema.partial({
  blockers: true,
  atRiskPredecessors: true,
});

export const MISSING_DATA_HEALTH_TYPES = {
  MISSING_ESTIMATES: 'MISSING_ESTIMATES',
  NO_TEAM_ASSIGNED: 'NO_TEAM_ASSIGNED',
  DISCONNECTED_FROM_STRATEGY: 'DISCONNECTED_FROM_STRATEGY',
} as const;

export type MissingDataHealthType =
  (typeof MISSING_DATA_HEALTH_TYPES)[keyof typeof MISSING_DATA_HEALTH_TYPES];

export const ITEM_HEALTH_RISK_TYPES = {
  PLANNED_START_DATE_PASSED: 'PLANNED_START_DATE_PASSED',
  PLANNED_FINISH_DATE_PASSED: 'PLANNED_FINISH_DATE_PASSED',
  UNPLANNED_WORK: 'UNPLANNED_WORK',
  DELTA_DIFF_LARGER_THAN_FIVE_PERCENT: 'DELTA_DIFF_LARGER_THAN_FIVE_PERCENT',
  DELTA_DIFF_LARGER_THAN_ONE_PERCENT: 'DELTA_DIFF_LARGER_THAN_ONE_PERCENT',
  BLOCKED_BY_DEPENDENCY: 'BLOCKED_BY_DEPENDENCY',
} as const;

export type ItemHealthRiskType =
  (typeof ITEM_HEALTH_RISK_TYPES)[keyof typeof ITEM_HEALTH_RISK_TYPES];

export const DEPENDENCY_HEALTH_TYPES = {
  BLOCKED_BY_ITEMS: 'BLOCKED_BY_ITEMS',
} as const;

type DependencyHealthType = (typeof DEPENDENCY_HEALTH_TYPES)[keyof typeof DEPENDENCY_HEALTH_TYPES];

export type DependencyItemCauseSummary = z.infer<typeof dependencyItemCauseSchema>;

export type MissingDataHealthTypeMetrics = Record<MissingDataHealthType, number[]>;
export type DependencyHealthTypeMetrics = Record<
  DependencyHealthType,
  DependencyItemCauseSummary[]
>;

export const ItemHealthLevel = {
  HEALTHY: 'HEALTHY',
  NEEDS_ATTENTION: 'NEEDS_ATTENTION',
  ACTION_REQUIRED: 'ACTION_REQUIRED',
  DONE: 'DONE',
} as const;

export type ItemHealthLevel = (typeof ItemHealthLevel)[keyof typeof ItemHealthLevel];

export const itemHealthLevelSchema = z.enum([
  ItemHealthLevel.HEALTHY,
  ItemHealthLevel.NEEDS_ATTENTION,
  ItemHealthLevel.ACTION_REQUIRED,
  ItemHealthLevel.DONE,
]);

export type HealthLevelCounts = Record<ItemHealthLevel, number>;

export type HealthRiskDetail = z.infer<typeof healthRiskSummarySchema>;

export type HealthRisks = Record<ItemHealthRiskType, HealthRiskDetail>;

export const itemsHealthMetricsResponseSchema = itemsHealthMetricsSchema
  .extend({
    dependency: z.record(z.string(), z.array(dependencyItemCauseSchema)),
  })
  .transform((data) => ({
    ...data,
    missingData: data.missingData as MissingDataHealthTypeMetrics,
    dependency: data.dependency as DependencyHealthTypeMetrics,
    healthLevels: data.healthLevels as HealthLevelCounts,
    healthRisks: data.healthRisks as HealthRisks,
    percentageDiff: data.percentageDiff ?? null,
    averageProgressPercentage: data.averageProgressPercentage ?? null,
  }));

export type ItemsHealthMetrics = z.infer<typeof itemsHealthMetricsResponseSchema>;

export const ItemDependencyStatus = {
  NO_ISSUE: 'NO_ISSUE',
  AT_RISK: 'AT_RISK',
  BLOCKED: 'BLOCKED',
  CANNOT_EVALUATE: 'CANNOT_EVALUATE',
} as const;

export type ItemDependencyStatus = (typeof ItemDependencyStatus)[keyof typeof ItemDependencyStatus];

export type ItemDependencyState = z.infer<typeof itemDependencyStateSchema>;

export type ItemHealthRisk = z.infer<typeof itemHealthRiskSchema>;

export type ItemHealth = z.infer<typeof itemHealthResponseSchema>;

const getItemDependencyStatus = (itemHealth: ItemHealth): ItemDependencyStatus =>
  itemHealth.dependency?.status ?? ItemDependencyStatus.NO_ISSUE;

export const isItemDependencyBlocked = (itemHealth: ItemHealth): boolean =>
  getItemDependencyStatus(itemHealth) === ItemDependencyStatus.BLOCKED;

export const isItemDependencyAtRisk = (itemHealth: ItemHealth): boolean =>
  (itemHealth.dependency?.atRiskPredecessorIds.length ?? 0) > 0 ||
  getItemDependencyStatus(itemHealth) === ItemDependencyStatus.AT_RISK;

export interface ItemHealthByItemIdsRequest {
  itemIds: number[];
}

export type ItemHealthByItemIdsResponse = z.infer<typeof itemHealthByItemIdsResponseSchema>;
